import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { Loader2 } from "lucide-react";
import SEO from "../components/SEO";
import { Eyebrow } from "../components/SectionHeading";
import { API } from "../lib/api";

const DynamicPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`${API}/site/pages/${slug}`);
        if (!cancelled) setPage(data);
      } catch (e) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-[#0A192F]/40">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading...
      </div>
    );
  }
  if (notFound || !page) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-5" data-testid="dynamic-page-not-found">
        <div className="text-center max-w-md">
          <h1 className="font-display font-black text-4xl text-[#0A192F] tracking-tighter">Page not found</h1>
          <p className="mt-3 text-[#0A192F]/70">This page doesn't exist or hasn't been published yet.</p>
        </div>
      </div>
    );
  }

  const cleanHtml = DOMPurify.sanitize(page.content_html || "", {
    ADD_ATTR: ["target", "rel"],
  });

  return (
    <div data-testid="dynamic-page">
      <SEO title={`${page.title} - CM Lindley`} path={`/pages/${page.slug}`} description={page.seo_description || page.title} />
      <section className="bg-[#0A192F] py-20 sm:py-24 lg:py-28" data-testid="dynamic-page-header">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
          <Eyebrow light>Page</Eyebrow>
          <h1 className="mt-5 font-display font-black tracking-tighter text-white text-4xl sm:text-6xl lg:text-7xl max-w-4xl">
            {page.title}
          </h1>
        </div>
      </section>
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-10">
          <div
            className="prose prose-lg max-w-none text-[#0A192F]"
            data-testid="dynamic-page-content"
            dangerouslySetInnerHTML={{ __html: cleanHtml }}
          />
        </div>
      </section>
    </div>
  );
};

export default DynamicPage;
